import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthLayout, FieldError } from '@/components/auth/AuthLayout';
import { api } from '@/lib/customFetch';
import { resetPasswordSchema, type ResetPasswordInput } from '@/lib/auth-schemas';

type ResetPasswordResponseDto = {
  reset: boolean;
  state?: string;
  pendingToken?: string;
};

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const email = params.get('email') ?? '';

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token, email },
  });

  useEffect(() => {
    setValue('token', token);
    setValue('email', email);
  }, [token, email, setValue]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      const response = await api.post<ResetPasswordResponseDto>('/auth/reset-password', {
        token: values.token,
        email: values.email,
        newPassword: values.password,
      });
      toast.success('Senha redefinida.');
      if (response.state === 'mfa-setup-required' && response.pendingToken) {
        navigate('/mfa/setup', { state: { pendingToken: response.pendingToken } });
        return;
      }
      navigate('/login');
    } catch {
      // toast já mostrado pelo customFetch
    }
  });

  if (!token) {
    return (
      <AuthLayout eyebrow="Redefinição de senha">
        <h2 style={{ fontSize: 26, fontWeight: 600, marginBottom: 8 }}>Token ausente</h2>
        <p style={{ color: 'var(--muted-foreground)', fontSize: 14, marginBottom: 24 }}>
          Abra o link enviado por email para redefinir sua senha.
        </p>
        <Link to="/forgot-password" className="text-sm hover:underline">
          Solicitar novo link
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow="Redefinição de senha">
      <h2 style={{ fontSize: 26, fontWeight: 600, marginBottom: 24 }}>Nova senha</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <input type="hidden" {...register('token')} />
        <input type="hidden" {...register('email')} />
        <div>
          <Label htmlFor="password">Nova senha</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            {...register('password')}
          />
          {errors.password && <FieldError>{errors.password.message}</FieldError>}
        </div>
        <div>
          <Label htmlFor="confirmPassword">Confirmar senha</Label>
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            {...register('confirmPassword')}
          />
          {errors.confirmPassword && <FieldError>{errors.confirmPassword.message}</FieldError>}
        </div>
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          Redefinir senha
        </Button>
      </form>
    </AuthLayout>
  );
}
